import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {
  useAccountIdsToPublishTo,
  useAllPublishingParamsForAccount,
  usePublishedAccountUrlMap,
  usePublishingParams,
  usePublishingStatusForAccountWithMediaType,
  usePublishingStatusForAllAccounts,
  useRetryPublishingForAccount,
  useSocialMediaWizardPost,
  useStartPublishingForAccount,
} from '@Components/social-media/social-media.hooks';
import {LoadingStates} from '@Utils/loading.util';
import {SocialAccountLabel} from '@Components/social-media/components/social-account-label';
import {isAccountPlanLocked, isGoogleBusinessProfileAccount} from '@Components/social-media/account.vo';
import {Text, TextSize} from '@Components/text';
import {haveAllPublishingStatesLoaded} from '@Libraries/social-media-wizard-accounts-library';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {getAccountMediaTypeSubtitleSuffix} from '@Libraries/social-media-params.library';
import {formatScheduleDateTime} from '@Libraries/social-media-wizard-scheduling-library';
import {getPostPublishStatus, PostPublishStatus} from '@Libraries/social-media-publish-status.library';
import {assertNever} from '@Utils/assert-never.util';
import type {VoidFunction} from '@Utils/general.util';
import {
  initPublishedUrlMapForAccounts,
  initPublishingStatusMapForAccounts,
  initRetryPublishingMapForAccount,
  setRetryingStatusForAccountWithMediaType,
  updateTriggerPrevious,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {getPlanLockedAccountMessage, removeConnectAccountBroadcastChannel, runReconnectWithBroadcast} from '@Libraries/social-media-account.library';
import {goBackToEvent, goBackToEventFromOneClickPromotion, isEventOneClickPromotionFlow, isEventsSocialPostingFlow} from '@Libraries/social-media-event-promotion.library';
import {useHasSeenConfetti} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {getPublishStatusErrorMessage, getPublishStatusErrorTitle} from '@Components/social-media-wizard/social-media-wizard-library';
import {OnboardingConfettiExplosion} from '@Components/onboarding-confetti-explosion/onboarding-confetti-explosion';
import {PostPublishStatusRow} from '@Components/social-media-wizard/components/post-publish-status-row/post-publish-status-row';
import {PostPublishStatusPill} from '@Components/social-media-wizard/components/post-publish-status-row/components/post-publish-status-pill/post-publish-status-pill';
import {useAppDispatch} from '@/hooks';
import {GA4EventName, trackPublishingGA4Events} from '@Libraries/ga-events';
import type {PublishingPostProps} from './publishing-post.types';
import {USER_WAITING_MESSAGE_DELAY_MS} from './publishing-post.types';
import styles from './publishing-post.module.scss';

export function PublishingPost({account, publishingAction, platformMediaType = PlatformMediaType.POST}: PublishingPostProps): ReactElement {
  useStartPublishingForAccount(account.id, platformMediaType);
  useRetryPublishingForAccount(account.id, platformMediaType);
  const loadingState: LoadingStates = usePublishingStatusForAccountWithMediaType(account.id, platformMediaType);
  const post = useSocialMediaWizardPost();
  const publishingParams = usePublishingParams();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParamsForAccount = useAllPublishingParamsForAccount(account.id)[platformMediaType];
  const publishedAccountUrlMap = usePublishedAccountUrlMap();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const hasSeenConfetti = useHasSeenConfetti();
  const dispatch = useAppDispatch();
  const [retryingAccountId, setRetryingAccountId] = useState(0);
  const reconnectChannelRef = useRef<BroadcastChannel | undefined>(undefined);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showWaitingMessage, setShowWaitingMessage] = useState(false);

  const publishErrorMessage = publishingParamsForAccount?.publishErrorMessage ?? null;
  const status = getPostPublishStatus({publishingAction, loadingState, publishErrorMessage});

  useEffect((): void => {
    dispatch(initRetryPublishingMapForAccount());
    dispatch(initPublishingStatusMapForAccounts());
    dispatch(initPublishedUrlMapForAccounts());
  }, []);

  useEffect((): VoidFunction | undefined => {
    if (loadingState !== LoadingStates.LOADING) {
      setShowWaitingMessage(false);
      return undefined;
    }

    const waitingMessageHandle = window.setTimeout((): void => {
      setShowWaitingMessage(true);
    }, USER_WAITING_MESSAGE_DELAY_MS);

    return (): void => {
      window.clearTimeout(waitingMessageHandle);
    };
  }, [loadingState]);

  const releaseReconnectChannel = (): void => {
    removeConnectAccountBroadcastChannel(reconnectChannelRef.current);
    reconnectChannelRef.current = undefined;
  };

  const abortAccountRetry = (): void => {
    releaseReconnectChannel();
    setRetryingAccountId(0);
  };

  const finishAccountRetry = (): void => {
    releaseReconnectChannel();
    setRetryingAccountId(0);
    dispatch(
      setRetryingStatusForAccountWithMediaType({
        accountId: account.id,
        platformMediaType,
      })
    );
  };

  useEffect((): VoidFunction => {
    return (): void => {
      releaseReconnectChannel();
    };
  }, []);

  useDidUpdateEffect((): void => {
    if (haveAllPublishingStatesLoaded(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams)) {
      if (isEventsSocialPostingFlow()) {
        goBackToEvent();
        return;
      }

      if (isEventOneClickPromotionFlow()) {
        goBackToEventFromOneClickPromotion();
        return;
      }

      if (!hasSeenConfetti && publishingAction !== SocialPostPublishingAction.SAVE) {
        setShowConfetti(true);
        void window.PMW.writeLocal('socialmedia/seenSocialMediaWizardConfetti');
      }
    }
  }, [publishedAccountUrlMap, publishingStatusForAllAccounts]);

  const onViewPost = (): void => {
    if (account.id in publishedAccountUrlMap) {
      window.open(publishedAccountUrlMap[account.id][platformMediaType] ?? '', '_blank');
    }
  };

  const onContinueEditing = (): void => {
    dispatch(updateTriggerPrevious());
  };

  const onRetryPublishing = (): void => {
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_FIXTHIS);
    setRetryingAccountId(account.id);

    reconnectChannelRef.current = runReconnectWithBroadcast({
      idAccount: account.id,
      accountType: account.type,
      previousChannel: reconnectChannelRef.current,
      onSuccess: finishAccountRetry,
      onNothingHappened: abortAccountRetry,
      onFailure: abortAccountRetry,
    });
  };

  const getPublishedActionLabel = (): string | undefined => {
    if (isGoogleBusinessProfileAccount(account)) {
      return window.i18next.t('pmwjs_view_profile');
    }

    return undefined;
  };

  const getStatusActionHandler = (): VoidFunction | undefined => {
    switch (status) {
      case PostPublishStatus.PUBLISHED:
        return onViewPost;
      case PostPublishStatus.SCHEDULED:
      case PostPublishStatus.SAVED_DRAFT:
        return onContinueEditing;
      case PostPublishStatus.FAILED:
        return onRetryPublishing;
      case PostPublishStatus.PUBLISHING:
      case PostPublishStatus.SCHEDULING:
      case PostPublishStatus.SAVING_DRAFT:
        return undefined;
      default:
        return assertNever(status);
    }
  };

  const getFailedStatusDescription = (): string => {
    if (isAccountPlanLocked(account)) {
      return getPlanLockedAccountMessage();
    }

    return getPublishStatusErrorMessage(publishErrorMessage, account.type, publishingAction);
  };

  const getWaitingMessageElement = (): ReactElement | null => {
    if (!showWaitingMessage || loadingState !== LoadingStates.LOADING) {
      return null;
    }

    return <Text size={TextSize.SM} className={styles.publishResultWaitingMessage} val={window.i18next.t('pmwjs_files_may_take_minutes')} />;
  };

  const getConfettiElement = (): ReactElement | null => {
    if (!showConfetti) {
      return null;
    }

    return <OnboardingConfettiExplosion numShots={1} />;
  };

  return (
    <section className={styles.publishResultColumn}>
      <div className={styles.publishResultProfileRow}>
        <SocialAccountLabel
          account={account}
          showAccountDetails
          platformMediaType={platformMediaType}
          subtitleSuffix={getAccountMediaTypeSubtitleSuffix(publishingParams, account, platformMediaType)}
          className={styles.publishResultAccountLabel}
        />
        <PostPublishStatusPill status={status} />
      </div>
      <span aria-hidden className={styles.publishResultDivider} />
      <PostPublishStatusRow
        status={status}
        scheduledDateTimeLabel={formatScheduleDateTime(post)}
        publishedActionLabel={getPublishedActionLabel()}
        errorTitle={getPublishStatusErrorTitle(account.type)}
        errorDescription={getFailedStatusDescription()}
        isPlanLocked={isAccountPlanLocked(account)}
        isActionDisabled={retryingAccountId === account.id && status === PostPublishStatus.FAILED}
        onActionClick={getStatusActionHandler()}
      />
      {getWaitingMessageElement()}
      {getConfettiElement()}
    </section>
  );
}
